import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * BannerCarousel — auto-sliding banner strip for the customer home.
 * Pulls active banners from GET /api/banners (only ones within their
 * start/end date range are returned). Slides every 5 seconds. Every click
 * hits POST /api/banners/{id}/click before opening the ad's link.
 */
export default function BannerCarousel() {
  const [banners, setBanners] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get("/banners").then((r) => setBanners(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || banners.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % banners.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [paused, banners.length]);

  if (!banners.length) return null;

  const goTo = (i) => setIndex(((i % banners.length) + banners.length) % banners.length);

  const handleClick = async (banner) => {
    try {
      const { data } = await api.post(`/banners/${banner.banner_id}/click`);
      const dest = data?.link_url || banner.link_url;
      if (dest) window.open(dest, "_blank", "noopener,noreferrer");
    } catch {
      if (banner.link_url) window.open(banner.link_url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section
      className="mt-4 max-w-7xl mx-auto px-5 sm:px-8"
      data-testid="banner-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative overflow-hidden rounded-3xl aspect-[3/1] sm:aspect-[4/1] shadow-lg ring-1 ring-black/5">
        <div
          className="flex h-full transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${index * 100}%)`, width: `${banners.length * 100}%` }}
        >
          {banners.map((b) => (
            <button
              key={b.banner_id}
              data-testid={`banner-slide-${b.banner_id}`}
              onClick={() => handleClick(b)}
              className="relative flex-shrink-0 h-full block text-left group"
              style={{ width: `${100 / banners.length}%` }}
              aria-label={b.title || "Promotion"}
            >
              <img
                src={b.image_url}
                alt={b.title || "Promotion"}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
              />
              {b.title && (
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/10 to-transparent flex items-center px-6 sm:px-10">
                  <div className="text-white drop-shadow-lg">
                    <div className="text-[10px] uppercase tracking-[0.35em] font-bold text-eatup-yellow">Sponsored</div>
                    <div className="font-display text-xl sm:text-3xl font-black mt-1 max-w-md">{b.title}</div>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Dots */}
        {banners.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid="banner-dots">
            {banners.map((b, i) => (
              <button
                key={b.banner_id}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                data-testid={`banner-dot-${i}`}
                aria-label={`Go to banner ${i + 1}`}
                className={`transition-all rounded-full ${i === index ? "w-6 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/60 hover:bg-white/80"}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
